export type Disposer = () => void;

export type EngineErrorCode =
  | 'engine_disposed'
  | 'engine_not_started'
  | 'phone_not_ready'
  | 'invalid_destination'
  | 'invalid_digits'
  | 'invalid_argument'
  | 'capability_unavailable'
  | 'pstn_not_enabled'
  | 'call_not_found'
  | 'invalid_call_state'
  | 'action_not_allowed'
  | 'busy'
  | 'microphone_denied'
  | 'microphone_not_found'
  | 'microphone_in_use'
  | 'microphone_unavailable'
  | 'insecure_context'
  | 'webrtc_unsupported'
  | 'output_selection_unsupported'
  | 'output_device_unavailable'
  | 'input_device_unavailable'
  | 'answer_failed'
  | 'call_ended'
  | 'server_rejected'
  | 'server_conflict'
  | 'server_unreachable'
  | 'command_unconfirmed'
  | 'consent_required'
  | 'capture_unsupported'
  | 'capture_unavailable'
  | 'capture_failed'
  | 'playback_blocked';

export interface EngineError {
  readonly code: EngineErrorCode;
  readonly message: string;
  readonly retryable: boolean;
  /** Server error code when the failure came from the API (for example `pstn_disabled`). */
  readonly server_code?: string;
}

export type Result<T = void> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: EngineError };

export const ok = <T>(value: T): Result<T> => ({ ok: true, value });
export const okVoid: Result<void> = Object.freeze({ ok: true, value: undefined });

export function engineError(code: EngineErrorCode, message: string, retryable = false, serverCode?: string): EngineError {
  return Object.freeze(serverCode === undefined ? { code, message, retryable } : { code, message, retryable, server_code: serverCode });
}

export function fail<T = never>(code: EngineErrorCode, message: string, retryable = false, serverCode?: string): Result<T> {
  return { ok: false, error: engineError(code, message, retryable, serverCode) };
}
